import { atom, ExtractAtomValue } from 'jotai';
import { atomWithStorage, RESET } from 'jotai/utils';

import { documentTransientAtom } from './document-transient';

import type {
  ContentReference,
  SearchDocument as Document,
} from '@/_generated/api-clients/xp';

export type RoutedFrom = 'search' | 'browse' | 'link';

export interface SetDocumentActionPayload {
  document: Document | null;
  routedFrom: RoutedFrom;
  icon?: string;
  backUrl?: string;
  country?: ContentReference['country'];
}

export const documentPersistedAtom = atomWithStorage('app.document.persisted', {
  document: null as Document | null,
  routedFrom: 'link' as RoutedFrom,
  icon: 'placeholder',
  isTridion: false,
  hasTranslatedValue: false,
  categoryName: '',
  subcategoryName: '',
  backUrl: '' as string | undefined,
});

/** Complex action: sets document and coordinates with transient state. */
export const setDocumentAtom = atom(
  null,
  (get, set, payload: SetDocumentActionPayload) => {
    const prev = get(documentPersistedAtom);
    const { icon, routedFrom, document, backUrl, country } = payload;
    const isFromLink = routedFrom === 'link';
    if (isFromLink && !document) return;

    set(documentPersistedAtom, {
      ...prev,
      routedFrom,
      icon: icon ?? 'placeholder',
      backUrl,
      isTridion: document?.sourceSystem === 'TRIDION',
      hasTranslatedValue:
        !!document?.otherContentUrl && document.sourceSystem === 'TRIDION',
      document: isFromLink ? prev.document : document,
    });

    set(documentTransientAtom, {
      ...get(documentTransientAtom),
      chosenLinkDocument: document,
      chosenDocumentCountry: country ?? null,
      chosenCountryTab: null,
    });
  },
);

/** Complex action: clears document and resets transient state. */
export const clearDocumentAtom = atom(null, (_, set) => {
  set(documentPersistedAtom, RESET);
  set(documentTransientAtom, RESET);
});

/** Direct partial update atom (like adminAtom pattern). */
export const updateDocumentPersistedAtom = atom(
  null,
  (
    get,
    set,
    partial: Partial<ExtractAtomValue<typeof documentPersistedAtom>>,
  ) => {
    const prev = get(documentPersistedAtom);
    set(documentPersistedAtom, {
      ...prev,
      ...partial,
      icon: 'icon' in partial ? (partial.icon ?? 'placeholder') : prev.icon,
    });
  },
);

/** Global document. The document state is either persisted or not based on if it comes from a direct link. */
export const documentFromDashboardOrResourceAtom = atom(get => {
  const persisted = get(documentPersistedAtom);
  const transient = get(documentTransientAtom);

  if (persisted.routedFrom === 'link') {
    return transient.chosenLinkDocument;
  } else {
    return persisted.document;
  }
});

export const translatedDocumentNameAtom = atom(get => {
  const document = get(documentFromDashboardOrResourceAtom);
  const transient = get(documentTransientAtom);
  return transient.isTranslating ? document?.otherName : document?.name;
});
